import { PayloadAction } from "@reduxjs/toolkit";

import { FormId } from "../../../../components/enums";
import {
  createGenericUISlice,
  GenericState,
  UIState,
} from "../../../../store/HOC/genericUI.slice";

import { ComponentId as AdditionalComponentId } from "./AdditionalData/enums";
import { ComponentId as ImagesDescriptionComponentId } from "./ImagesDescriptionData/enums";

export type FormDataInputs = {
  [FormId.DAMAGES_IMAGES_DESCRIPTION]: {
    [ImagesDescriptionComponentId.DESCRIPTION]: string;
  };
  [FormId.DAMAGES_ADDITIONAL]: {
    [AdditionalComponentId.ARE_PREVIOUS_DAMAGE_PRESENT]: string;
    [AdditionalComponentId.PREVIOUS_DAMAGE_DESCRIPTION]: string;
    [AdditionalComponentId.AIRBAGS_DEPLOYED]: string;
    [AdditionalComponentId.IS_REPLACEMENT_VEHICLE_REQUIRED]: string;
    [AdditionalComponentId.IS_VEHICLE_WORKING]: string;
    [AdditionalComponentId.ARE_OTHER_DAMAGES_PRESENT]: string;
    [AdditionalComponentId.OTHER_DAMAGES_DESCRIPTION]: string;
  };
};

export type DamagesImagesDescriptionFormInputs = FormDataInputs[FormId.DAMAGES_IMAGES_DESCRIPTION];
export type DamagesAdditionalFormInputs = FormDataInputs[FormId.DAMAGES_ADDITIONAL];

type VehicleOwnerState = {
  data: FormDataInputs;
  ui: UIState;
};

const initialState: VehicleOwnerState = {
  data: {
    [FormId.DAMAGES_IMAGES_DESCRIPTION]: {
      [ImagesDescriptionComponentId.DESCRIPTION]: "",
    },
    [FormId.DAMAGES_ADDITIONAL]: {
      [AdditionalComponentId.ARE_PREVIOUS_DAMAGE_PRESENT]: "",
      [AdditionalComponentId.PREVIOUS_DAMAGE_DESCRIPTION]: "",
      [AdditionalComponentId.AIRBAGS_DEPLOYED]: "",
      [AdditionalComponentId.IS_REPLACEMENT_VEHICLE_REQUIRED]: "",
      [AdditionalComponentId.IS_VEHICLE_WORKING]: "",
      [AdditionalComponentId.ARE_OTHER_DAMAGES_PRESENT]: "",
      [AdditionalComponentId.OTHER_DAMAGES_DESCRIPTION]: "",
    },
  },
  ui: {
    validated: false,
  },
};

const damagesSlice = createGenericUISlice({
  name: "@OC/vehicle/damages",
  initialState: initialState as GenericState<FormDataInputs>,
  reducers: {
    setData: (state, action: PayloadAction<FormDataInputs>) => {
      state.data[FormId.DAMAGES_IMAGES_DESCRIPTION] = {
        ...initialState.data[FormId.DAMAGES_IMAGES_DESCRIPTION],
        ...action.payload[FormId.DAMAGES_IMAGES_DESCRIPTION],
      };
      state.data[FormId.DAMAGES_ADDITIONAL] = {
        ...initialState.data[FormId.DAMAGES_ADDITIONAL],
        ...action.payload[FormId.DAMAGES_ADDITIONAL],
      };
      state.ui.validated = true;
    },
    setPreviousDamagePresent: (state, action: PayloadAction<string>) => {
      state.data[FormId.DAMAGES_ADDITIONAL][
        AdditionalComponentId.ARE_PREVIOUS_DAMAGE_PRESENT
      ] = action.payload;
    },
    setIsVehicleWorking: (state, action: PayloadAction<string>) => {
      state.data[FormId.DAMAGES_ADDITIONAL][
        AdditionalComponentId.IS_VEHICLE_WORKING
      ] = action.payload;
      state.data[FormId.DAMAGES_ADDITIONAL][
        AdditionalComponentId.IS_REPLACEMENT_VEHICLE_REQUIRED
      ] =
        initialState.data[FormId.DAMAGES_ADDITIONAL][
          AdditionalComponentId.IS_REPLACEMENT_VEHICLE_REQUIRED
        ];
    },
    setIsReplacementVehicleRequired: (state, action: PayloadAction<string>) => {
      state.data[FormId.DAMAGES_ADDITIONAL][
        AdditionalComponentId.IS_REPLACEMENT_VEHICLE_REQUIRED
      ] = action.payload;
    },
    setAreOtherDamagesPresent: (state, action: PayloadAction<string>) => {
      state.data[FormId.DAMAGES_ADDITIONAL][
        AdditionalComponentId.ARE_OTHER_DAMAGES_PRESENT
      ] = action.payload;
    },
  },
});

export const {
  setData: setDamagesFormData,
  setAreOtherDamagesPresent,
  setPreviousDamagePresent,
  setIsReplacementVehicleRequired,
  setIsVehicleWorking,
} = damagesSlice.actions;

export const { reducer } = damagesSlice;
